import { useEffect, useRef, useState } from "react";
import { useVoiceCall, CallStatus } from "@aihumanity/voice-sdk/react";

// ── Constants ─────────────────────────────────────────────────────────────────

const VOICE_API_URL = process.env.REACT_APP_VOICE_API_URL || "";

// Derive WebSocket base: https://… → wss://…  |  http://… → ws://…
function toWsBase(httpUrl) {
  return httpUrl.replace(/^https:\/\//i, "wss://").replace(/^http:\/\//i, "ws://");
}

// ── Emotion metadata ──────────────────────────────────────────────────────────

const EMOTION_META = {
  happy:     { color: "#4ade80", label: "Happy" },
  excited:   { color: "#facc15", label: "Excited" },
  joy:       { color: "#4ade80", label: "Joyful" },
  confident: { color: "#6ddcff", label: "Confident" },
  neutral:   { color: "#a0a0b8", label: "Neutral" },
  calm:      { color: "#93c5fd", label: "Calm" },
  curious:   { color: "#c4b5fd", label: "Curious" },
  sad:       { color: "#818cf8", label: "Sad" },
  anxious:   { color: "#fb923c", label: "Anxious" },
  stressed:  { color: "#fb923c", label: "Stressed" },
  angry:     { color: "#f87171", label: "Angry" },
  fear:      { color: "#f87171", label: "Fearful" },
  afraid:    { color: "#f87171", label: "Afraid" },
  disgust:   { color: "#fb923c", label: "Disgust" },
  surprised: { color: "#facc15", label: "Surprised" },
};

function emotionMeta(key) {
  const k = (key || "").toLowerCase();
  return (
    EMOTION_META[k] || {
      color: "#a0a0b8",
      label: k.charAt(0).toUpperCase() + k.slice(1),
    }
  );
}

// ── Visual helpers ────────────────────────────────────────────────────────────

function Waveform({ active }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3, height: 24 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            width: 3, borderRadius: 2,
            background: "linear-gradient(180deg, #6ddcff, #7e60f8)",
            height: active ? `${10 + Math.sin(i) * 8}px` : 4,
            animation: active ? `wave ${0.6 + i * 0.1}s ease-in-out infinite alternate` : "none",
          }}
        />
      ))}
      <style>{`@keyframes wave{from{transform:scaleY(1)}to{transform:scaleY(2.2)}}`}</style>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: 20, height: 20,
      border: "2px solid #2a2a3a", borderTop: "2px solid #6ddcff",
      borderRadius: "50%", animation: "spin 0.8s linear infinite",
    }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function EmotionTag({ emotion }) {
  const meta   = emotionMeta(emotion);
  const bg     = meta.color + "1a";
  const border = meta.color + "47";
  return (
    <>
      <style>{`
        @keyframes emotionPop{0%{transform:scale(0.85);opacity:0}60%{transform:scale(1.06)}100%{transform:scale(1);opacity:1}}
        @keyframes emotionDot{0%,100%{opacity:1}50%{opacity:0.3}}
      `}</style>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: bg, border: `1px solid ${border}`,
        borderRadius: 20, padding: "5px 12px", marginBottom: 8,
        animation: "emotionPop 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: "50%", background: meta.color,
          animation: "emotionDot 1.6s ease-in-out infinite", flexShrink: 0,
        }} />
        <span style={{
          fontFamily: "Manrope, sans-serif", fontSize: "0.7rem",
          fontWeight: 600, color: meta.color, letterSpacing: "0.04em",
        }}>
          {meta.label}
        </span>
      </div>
    </>
  );
}

/**
 * Live emotion bar chart.
 * scores: { happy: 0.72, anxious: 0.15, ... }
 * Shows top 7 emotions sorted by score; bars animate via CSS transition.
 */
function EmotionBarChart({ scores, topEmotion }) {
  if (!scores || typeof scores !== "object") return null;

  const entries = Object.entries(scores)
    .map(([k, v]) => ({ key: k, score: typeof v === "number" ? v : 0, ...emotionMeta(k) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 7);

  if (entries.length === 0) return null;

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)", border: "1px solid #1e1e2e",
      borderRadius: 12, padding: "12px 14px", marginBottom: 10,
    }}>
      <div style={{
        fontFamily: "Manrope, sans-serif", fontSize: "0.65rem",
        color: "#555", letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 8,
      }}>
        Vocal affect
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {entries.map(({ key, score, color, label }) => {
          const isTop = key === topEmotion;
          const pct   = Math.round(score * 100);
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                fontFamily: "Manrope, sans-serif", fontSize: "0.68rem",
                color: isTop ? color : "#666",
                fontWeight: isTop ? 700 : 400,
                width: 68, flexShrink: 0, textAlign: "right",
                transition: "color 0.4s",
              }}>
                {label}
              </div>
              <div style={{
                flex: 1, height: 6, borderRadius: 4,
                background: "rgba(255,255,255,0.06)", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  width: `${pct}%`,
                  background: isTop
                    ? `linear-gradient(90deg, ${color}99, ${color})`
                    : `${color}55`,
                  transition: "width 0.5s ease, background 0.4s ease",
                }} />
              </div>
              <div style={{
                fontFamily: "Manrope, sans-serif", fontSize: "0.65rem",
                color: isTop ? color : "#444",
                width: 28, flexShrink: 0, textAlign: "right",
                transition: "color 0.4s",
              }}>
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Call options (stable ref, outside component) ──────────────────────────────

async function fetchJoinUrl() {
  const res = await fetch("/.netlify/functions/create-call", { method: "POST" });
  if (!res.ok) throw new Error("Could not create call session.");
  return res.json();
}

const VOICE_OPTS = { fetchJoinUrl };

// ── Component ─────────────────────────────────────────────────────────────────

export default function FloatingDavid() {
  const [open, setOpen] = useState(false);

  // Live emotion state driven by WebSocket
  const [liveScores, setLiveScores]   = useState(null);
  const [liveEmotion, setLiveEmotion] = useState(null);
  const wsRef = useRef(null);

  const {
    status, isBusy, isLive,
    lastEmotion, contactSaved, micMuted,
    callId, sessionToken,
    error, start, end, toggleMicMute,
  } = useVoiceCall(VOICE_OPTS);

  const isConnecting = status === CallStatus.CONNECTING;

  // ── Emotion WebSocket ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLive || !callId || !sessionToken || !VOICE_API_URL) return;

    const url = `${toWsBase(VOICE_API_URL)}/v1/voice/emotion/stream` +
      `?callId=${encodeURIComponent(callId)}&token=${encodeURIComponent(sessionToken)}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "emotion") {
          if (msg.scores)  setLiveScores(msg.scores);
          if (msg.emotion) setLiveEmotion(msg.emotion);
        }
      } catch { /* ignore */ }
    };

    ws.onerror = () => {
      console.warn("[FloatingDavid] emotion stream unavailable");
    };

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [isLive, callId, sessionToken]);

  // Clear emotion state when call ends
  useEffect(() => {
    if (!isLive) {
      setLiveScores(null);
      setLiveEmotion(null);
    }
  }, [isLive]);

  // Allow the header "Talk to founder" button to open and start the call
  useEffect(() => {
    function handleTalkEvent() {
      if (isLive || isBusy) return;
      setOpen(true);
      void start();
    }
    window.addEventListener("talkToDavid", handleTalkEvent);
    return () => window.removeEventListener("talkToDavid", handleTalkEvent);
  }, [isLive, isBusy, start]);

  function handleStart() {
    setOpen(true);
    void start();
  }

  function handleEnd() {
    void end();
  }

  // Prefer live WS emotion; fall back to SDK-polled lastEmotion
  const displayEmotion = liveEmotion || lastEmotion;

  return (
    <>
      {/* ── FAB ─────────────────────────────────────────────────────────── */}
      <button
        onClick={() => {
          if (isLive || isBusy) handleEnd();
          else setOpen((v) => !v);
        }}
        title={isLive ? "End call" : "Talk to David"}
        style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 1000,
          width: 60, height: 60, borderRadius: "50%", border: "none", cursor: "pointer",
          background: isLive
            ? "linear-gradient(135deg, #ff4d4d, #c0392b)"
            : "linear-gradient(135deg, #6ddcff, #7e60f8)",
          boxShadow: isLive
            ? "0 0 0 6px rgba(255,77,77,0.25), 0 4px 20px rgba(192,57,43,0.5)"
            : "0 4px 20px rgba(126,96,248,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.3s, box-shadow 0.3s",
          animation: isLive ? "fabPulse 2s ease-in-out infinite" : "none",
        }}
      >
        {isLive ? (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.58 19.79 19.79 0 0 1 1.36.94a2 2 0 0 1 2-.18l3 .01a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11z"/>
            <line x1="23" y1="1" x2="1" y2="23"/>
          </svg>
        ) : isBusy ? (
          <Spinner />
        ) : (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="11" rx="3" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <line x1="12" y1="19" x2="12" y2="22" />
            <line x1="8"  y1="22" x2="16" y2="22" />
          </svg>
        )}
        <style>{`@keyframes fabPulse{0%,100%{box-shadow:0 0 0 6px rgba(255,77,77,0.25),0 4px 20px rgba(192,57,43,0.5)}50%{box-shadow:0 0 0 10px rgba(255,77,77,0.1),0 4px 24px rgba(192,57,43,0.6)}}`}</style>
      </button>

      {/* ── Tooltip ─────────────────────────────────────────────────────── */}
      {!open && status === CallStatus.IDLE && (
        <div style={{
          position: "fixed", bottom: 36, right: 98, zIndex: 999,
          background: "#0d0d14", border: "1px solid #2a2a3a", borderRadius: 10,
          padding: "6px 12px", fontFamily: "Manrope, sans-serif", fontSize: "0.8rem",
          color: "#e0e0e0", pointerEvents: "none", whiteSpace: "nowrap",
        }}>
          Talk to David
        </div>
      )}

      {/* ── Panel ───────────────────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: "fixed", bottom: 100, right: 28, zIndex: 1000,
          width: 300, background: "#0d0d14", border: "1px solid #1e1e2e",
          borderRadius: 18, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg, rgba(109,220,255,0.08), rgba(126,96,248,0.12))",
            borderBottom: "1px solid #1e1e2e", padding: "16px 20px",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "Manrope, sans-serif", fontSize: "1rem", fontWeight: 700, color: "#fff",
              flexShrink: 0,
              boxShadow: isLive ? "0 0 0 3px rgba(109,220,255,0.3)" : "none",
              transition: "box-shadow 0.3s",
            }}>DC</div>
            <div>
              <div style={{ fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "#fff" }}>
                David Chiu
              </div>
              <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", color: "#7e60f8", letterSpacing: "0.08em", marginTop: 1 }}>
                Founder & CEO · AIHumanity
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#555", padding: 4, lineHeight: 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "20px", textAlign: "center" }}>

            {/* IDLE */}
            {status === CallStatus.IDLE && (
              <>
                <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", color: "#a0a0b8", lineHeight: 1.6, marginBottom: 20 }}>
                  Have a real-time voice conversation with David — ask about AIHumanity, the technology, or the raise.
                </p>
                {error && (
                  <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.78rem", color: "#ff6b6b", marginBottom: 12 }}>
                    {error.message}
                  </p>
                )}
                <button onClick={handleStart} style={{
                  width: "100%", padding: "12px", borderRadius: 12, border: "none", cursor: "pointer",
                  background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
                  fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                    <rect x="9" y="2" width="6" height="11" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                    <line x1="8"  y1="22" x2="16" y2="22" />
                  </svg>
                  Start Conversation
                </button>
              </>
            )}

            {/* CONNECTING / DISCONNECTING */}
            {isBusy && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "12px 0" }}>
                <Spinner />
                <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", color: "#a0a0b8" }}>
                  {isConnecting ? "Connecting to David…" : "Ending call…"}
                </p>
              </div>
            )}

            {/* LIVE */}
            {isLive && (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <Waveform active />
                </div>
                <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.82rem", color: "#6ddcff", marginBottom: 8, letterSpacing: "0.04em" }}>
                  Live · Speaking with David
                </p>

                {/* Contact saved badge */}
                {contactSaved && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(109,220,255,0.08)", border: "1px solid rgba(109,220,255,0.2)",
                    borderRadius: 20, padding: "4px 12px", marginBottom: 10,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="2.5" strokeLinecap="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", color: "#6ddcff" }}>
                      Contact info saved
                    </span>
                  </div>
                )}

                {/* Dominant emotion tag */}
                {displayEmotion && <EmotionTag emotion={displayEmotion} />}

                {/* Live per-emotion bar chart */}
                <EmotionBarChart scores={liveScores} topEmotion={liveEmotion} />

                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  {/* Mute */}
                  <button onClick={toggleMicMute} style={{
                    flex: 1, padding: "10px", borderRadius: 10,
                    border: "1px solid #2a2a3a", cursor: "pointer",
                    background: micMuted ? "#2a2a3a" : "transparent",
                    fontFamily: "Manrope, sans-serif", fontSize: "0.82rem",
                    color: micMuted ? "#6ddcff" : "#a0a0b8",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      {micMuted ? (
                        <>
                          <line x1="1" y1="1" x2="23" y2="23" />
                          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                          <line x1="12" y1="19" x2="12" y2="22" />
                        </>
                      ) : (
                        <>
                          <rect x="9" y="2" width="6" height="11" rx="3" />
                          <path d="M5 10a7 7 0 0 0 14 0" />
                          <line x1="12" y1="19" x2="12" y2="22" />
                        </>
                      )}
                    </svg>
                    {micMuted ? "Unmute" : "Mute"}
                  </button>

                  {/* End call */}
                  <button onClick={handleEnd} style={{
                    flex: 1, padding: "10px", borderRadius: 10, border: "none",
                    cursor: "pointer", background: "#3d1a1a",
                    fontFamily: "Manrope, sans-serif", fontSize: "0.82rem", color: "#ff6b6b",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.58 19.79 19.79 0 0 1 1.36.94a2 2 0 0 1 2-.18l3 .01a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11z" />
                      <line x1="23" y1="1" x2="1" y2="23" />
                    </svg>
                    End Call
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {status === CallStatus.IDLE && (
            <div style={{
              borderTop: "1px solid #1a1a28", padding: "10px 20px",
              fontFamily: "Manrope, sans-serif", fontSize: "0.7rem", color: "#444", textAlign: "center",
            }}>
              Powered by AIHumanity · Ultravox voice AI
            </div>
          )}
        </div>
      )}
    </>
  );
}
